import { useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  AccountFundingOption,
  DefaultBankAccountList,
  formattedCurrency,
  FundingSource,
  FundingTrigger,
  sanitizeToNumeric,
  SupportedCurrencies,
  TransactionType,
} from 'vopay';
import { InfoBanner } from '@/components/UI';
import {
  getAvailableBalance,
  getFundingSettlementTimeLabel,
} from '@/hooks/useFields/TransactionCreationFields/useFundingOptionField';
import useFetchWorkflows from '@/hooks/useApi/useFetchWorkflows';
import useTransactionApprovalAccess from '@/hooks/useTransactionApprovalAccess';
import { useToast } from '@/providers/Toast.provider';

type ElinxFundingBannerProps = {
  fundingTrigger: FundingTrigger;
  isScheduled: boolean;
  fundingOptions: AccountFundingOption[];
  payFrom: FundingSource | '';
  amount: string;
  currency: SupportedCurrencies;
  operationalBankAccount?: DefaultBankAccountList;
};

// Conditional funding banner for the ePay creation form, per the eLinx Pay (ePay) Confluence spec —
// mirrors the Payments page's FundingBanner with the link clause added. Hidden for scheduled
// requests and whenever an active approval workflow matches.
export default function ElinxFundingBanner({
  fundingTrigger,
  isScheduled,
  fundingOptions,
  payFrom,
  amount,
  currency,
  operationalBankAccount,
}: ElinxFundingBannerProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.EPay' });
  const { workflows, fetchWorkflows } = useFetchWorkflows();
  const canUseTransactionApproval = useTransactionApprovalAccess();
  const toast = useToast();

  useEffect(() => {
    if (!canUseTransactionApproval) return;

    (async () => {
      try {
        await fetchWorkflows();
      } catch {
        toast.neutral(t('fetchWorkflowsError', { defaultValue: 'Could not check approval workflows' }));
      }
    })();
  }, [canUseTransactionApproval]);

  const selectedFundingOption = useMemo(
    () => fundingOptions.find((option) => option.Source === payFrom),
    [fundingOptions, payFrom],
  );

  const balanceOption = useMemo(() => fundingOptions.find((option) => option.Source === 'balance'), [fundingOptions]);

  const hasPositiveBalance = getAvailableBalance(balanceOption) > 0;

  // Mirrors the backend's workflow match: an eLinx request's approval workflow is always keyed to
  // the eLinx transaction type, regardless of the receiver's chosen payment method.
  const requiresApproval = useMemo(() => {
    if (!amount) return false;

    const group = workflows.find((w) => w.TransactionType === TransactionType.ELINX);
    if (!group) return false;

    const numericAmount = Number(sanitizeToNumeric(amount));
    return group.Workflows.some(
      (workflow) =>
        workflow.IsActive &&
        numericAmount >= Number(workflow.MinAmount) &&
        (workflow.MaxAmount === null || numericAmount < Number(workflow.MaxAmount)),
    );
  }, [workflows, amount]);

  const banner = useMemo(() => {
    if (isScheduled || requiresApproval) return undefined;

    if (fundingTrigger === 'never') {
      if (!selectedFundingOption) return undefined;

      if (selectedFundingOption.Source === 'bank') {
        return { i18nKey: 'FundingBanner.bankAccount', values: {} };
      }

      if (selectedFundingOption.Source === 'credit') {
        const feePercent = Number(selectedFundingOption.FeePercent || 0);
        const feePerTransaction = Number(selectedFundingOption.FeePerTransaction || 0);
        const computedFee =
          feePercent > 0 ? (Number(sanitizeToNumeric(amount) || 0) * feePercent) / 100 : feePerTransaction;
        const feeAmount = `${selectedFundingOption.Currency} ${formattedCurrency(String(computedFee))}`;
        const settlementTime = getFundingSettlementTimeLabel(t, selectedFundingOption.SettlementTime);

        return { i18nKey: 'FundingBanner.creditCard', values: { feeAmount, settlementTime } };
      }

      return undefined;
    }

    if (fundingTrigger === 'insufficient-balance' || fundingTrigger === 'always') {
      if (!operationalBankAccount || Number(sanitizeToNumeric(amount)) <= 0) return undefined;

      const isShort = Number(sanitizeToNumeric(amount)) > getAvailableBalance(balanceOption);
      if (fundingTrigger === 'insufficient-balance' && !isShort) return undefined;

      const fundingAmount = `${currency} ${formattedCurrency(amount)}`;
      const bankAccount = `${operationalBankAccount.InstitutionName} ${operationalBankAccount.AccountNumber}`;

      return { i18nKey: 'FundingBanner.autoFunded', values: { amount: fundingAmount, bankAccount } };
    }

    return undefined;
  }, [
    fundingTrigger,
    isScheduled,
    requiresApproval,
    selectedFundingOption,
    balanceOption,
    amount,
    currency,
    operationalBankAccount,
    t,
  ]);

  if (!banner) return null;

  const balanceNoteKey = `${banner.i18nKey}BalanceNote`;

  return (
    <InfoBanner colorScheme="neutral">
      <Trans
        t={t}
        i18nKey={banner.i18nKey}
        values={banner.values}
        defaults={ELINX_FUNDING_BANNER_DEFAULTS[banner.i18nKey]}
        components={{ bold: <span style={{ fontWeight: 600 }} /> }}
      />
      {hasPositiveBalance && (
        <Trans
          t={t}
          i18nKey={balanceNoteKey}
          defaults={ELINX_FUNDING_BANNER_DEFAULTS[balanceNoteKey]}
          components={{ bold: <span style={{ fontWeight: 600 }} /> }}
        />
      )}
    </InfoBanner>
  );
}

const ELINX_FUNDING_BANNER_DEFAULTS: Record<string, string> = {
  'FundingBanner.bankAccount':
    'This request will stay in <bold>Waiting</bold> until the funding settles. The payment link will then be ready to use, or sent out automatically depending on your eLinx notification settings.',
  'FundingBanner.bankAccountBalanceNote': " Your VoPay balance won't be used — the funding covers the full payment.",
  'FundingBanner.creditCard':
    'This request will remain <bold>Waiting</bold> until the funding settles. An additional fee of <bold>{{feeAmount}}</bold> will be charged, and the funding is expected to settle within <bold>{{settlementTime}}</bold>; the payment link will then be ready to use, or sent out automatically depending on your eLinx notification settings.',
  'FundingBanner.creditCardBalanceNote':
    ' Your VoPay balance will not be used, as the funding covers the full payment amount.',
  'FundingBanner.autoFunded':
    'A funding transaction for <bold>{{amount}}</bold> will first be created to withdraw funds from <bold>{{bankAccount}}</bold>. This request will remain <bold>Waiting</bold> until the funds settle; the payment link will then be ready to use, or sent out automatically depending on your eLinx notification settings.',
  'FundingBanner.autoFundedBalanceNote': ' Your VoPay balance will not be used.',
};
